import { getAccount } from '@wagmi/core';
import type { GetAccountReturnType } from '@wagmi/core';
import { tryGetWagmiConfig } from './wagmi-client';

/**
 * The connector details the app consumes: the connected account and the chain it is on.
 */
export interface WalletConnectorInfo {
  account?: string;
  chainId?: number;
}

/**
 * Single source of truth for "which wallet is connected, and on which chain".
 *
 * @remarks
 * The app previously read `localStorage['wagmi.store']` directly in 48 places across 34 files,
 * each hard-coding wagmi v1's persisted shape (`state.data.account`). wagmi v2 stores a
 * different shape entirely, so those reads would all have silently returned `undefined` after
 * the upgrade. Routing them through here meant the upgrade only had to change this file.
 *
 * This now reads wagmi's live in-memory state rather than its persisted blob, which is also
 * more accurate: the persisted copy can lag behind a wallet-side account or chain switch.
 *
 * @returns the connector info, or undefined when no wallet is connected
 */
export function getWalletConnectorInfo(): WalletConnectorInfo | undefined {
  const config = tryGetWagmiConfig();
  if (!config) return undefined;

  const account = getAccount(config);
  if (!account?.address) return undefined;

  return { account: account.address, chainId: account.chainId };
}

/**
 * Reads the connected wallet address.
 *
 * @remarks
 * Returns `undefined` rather than an empty string when nothing is connected, matching the
 * optional-chained reads this replaced so existing `?? ''` fallbacks keep behaving identically.
 * @returns the address, or undefined when no wallet is connected
 */
export function getWalletAddress(): string | undefined {
  return getWalletConnectorInfo()?.account;
}

/**
 * Reads the chain id the connected wallet is on.
 * @returns the chain id, or undefined when no wallet is connected
 */
export function getWalletChainId(): number | undefined {
  return getWalletConnectorInfo()?.chainId;
}

/**
 * Whether a wallet is connected *at this instant*.
 *
 * @remarks
 * Used by the callers that guarded on the presence of a stored wagmi session before doing
 * chain work, so they can keep refusing to proceed without a wallet.
 *
 * This is a point-in-time read, not a readiness check. It is false during `connecting` and
 * `reconnecting`, because wagmi has no address to report while a handshake is in flight — so a
 * connected user reads as disconnected for the whole of a page load, until the restore settles.
 * Treating a single false here as "the user is logged out" is a mistake this codebase has made
 * more than once.
 *
 * For a guard that runs at component startup, await `WagmiConfigService.waitForWalletSettled()`
 * instead, which resolves only once the status is settled. Use this function only where a
 * handshake cannot be in progress — for example on a form submit, long after boot. To branch on
 * the in-flight states explicitly, see {@link getWalletStatus}.
 * @returns true when an account is connected right now
 */
export function hasWalletSession(): boolean {
  return !!getWalletConnectorInfo();
}

/**
 * Reads wagmi's connection status.
 *
 * @remarks
 * Exposed so callers can tell a settled `disconnected` apart from an in-flight
 * `connecting`/`reconnecting`, which {@link hasWalletSession} collapses into a single `false`.
 * @returns the status, or undefined when the wallet layer has not been initialised yet
 */
export function getWalletStatus(): GetAccountReturnType['status'] | undefined {
  const config = tryGetWagmiConfig();
  if (!config) return undefined;

  return getAccount(config).status;
}
